import type { FilterOption } from '@/components/common-table/FilterSearchBar'
import { FiltersSearchBar } from '@/components/common-table/FilterSearchBar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import React, { useState, useRef } from 'react'
import { useCustomViewsContext } from './CustomViewsContext'

interface CustomViewCreatorProps {
	isOpen: boolean
	onClose: () => void
	columns: Array<{ id: string; label: string; formatValue?: (value: unknown) => string; isTimestamp?: boolean }>
	data: Record<string, unknown>[]
	onSave: (name: string, filters: FilterOption[]) => void
	className?: string
	initialFilters?: FilterOption[]
	onOpenChange?: (isOpen: boolean) => void
	editingViewId?: string | null
	editingViewName?: string
	existingViewNames?: string[]
	onCancel?: () => void
}

export function CustomViewCreator({
	isOpen,
	onClose,
	onSave,
	className,
	columns,
	data,
	editingViewId = null,
	editingViewName = '',
	existingViewNames = [],
	onCancel,
}: CustomViewCreatorProps) {
	const [viewName, setViewName] = useState('')
	const [searchValue, setSearchValue] = useState('')
	const [error, setError] = useState<string | null>(null)
	const containerRef = useRef<HTMLDivElement | null>(null)

	const { setIsViewCreatorOpen, currentFilters, setCurrentFilters, rightContent } = useCustomViewsContext()

	React.useEffect(() => {
		setIsViewCreatorOpen(isOpen)
	}, [isOpen, setIsViewCreatorOpen])

	React.useEffect(() => {
		if (isOpen) {
			setError(null)
			if (editingViewId && editingViewName) {
				// Editing mode - set the name from the editing view
				setViewName(editingViewName)
			} else {
				// Create mode - start with empty name
				setViewName('')
			}
		} else {
			setViewName('')
			setError(null)
		}
	}, [isOpen, editingViewId, editingViewName])

	const handleSave = () => {
		const trimmedName = viewName.trim()
		if (!trimmedName) {
			setError('Please enter a view name')
			return
		}

		const isDuplicate = existingViewNames?.some(name => name.toLowerCase() === trimmedName.toLowerCase())
		if (isDuplicate && trimmedName.toLowerCase() !== editingViewName?.toLowerCase()) {
			setError(`A custom view named "${trimmedName}" already exists. Please choose a different name.`)
			return
		}

		if (currentFilters.length === 0) {
			//show error msg when no filters are applied
			setError('Please apply at least one filter before saving the view')
			return
		}
		setError(null)
		onSave(trimmedName, currentFilters) // ✅ Use currentFilters
		setViewName('')
		setCurrentFilters([])
		onClose()
	}

	const handleCancel = () => {
		setViewName('')
		setError(null)
		setCurrentFilters([])
		onCancel?.()
		onClose()
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			handleSave()
		} else if (e.key === 'Escape') {
			e.preventDefault()
			handleCancel()
		}
	}

	if (!isOpen) {
		return null
	}

	return (
		<div ref={containerRef} className={cn('w-full relative bg-sidebar p-4 space-y-3 z-[98]', className)}>
			{/* View Name Input */}
			<div className='flex items-start justify-between gap-2'>
				<div className='min-w-(--search-width) w-fit max-w-full flex flex-col gap-1'>
					<Input
						value={viewName}
						onChange={e => {
							setViewName(e.target.value)
							if (error) {
								setError(null)
							}
						}}
						onKeyDown={handleKeyDown}
						placeholder='Enter view name...'
						className='w-full h-(--height-control) text-body'
						autoFocus={isOpen}
					/>
					{error && <div className='text-body text-destructive text-sm mt-1'>{error}</div>}
				</div>
				<div className='flex items-center justify-end gap-2'>
					<Button variant='ghost' size='sm' onClick={handleCancel} className='h-control text-control'>
						Cancel
					</Button>
					<Button
						variant='default'
						size='sm'
						onClick={handleSave}
						disabled={!viewName.trim()}
						className='h-control text-control'>
						Save
					</Button>
				</div>
			</div>

			<div className='flex items-center gap-3'>
				<div className='flex-1 min-w-0'>
					<FiltersSearchBar
						value={searchValue}
						onChange={setSearchValue}
						placeholder='Search and filter...'
						columns={columns}
						data={data}
						filters={currentFilters}
						onFiltersChange={newFilters => {
							setCurrentFilters(newFilters)
							if (error) {
								setError(null)
							}
						}}
						insideModal={true}
						showSaveButton={false}
					/>
				</div>
				{/* Right content slot for display properties icon */}
				{rightContent}
			</div>
		</div>
	)
}
